/* eslint-disable @typescript-eslint/no-empty-function */
import { CategorizedResults, CategoryMap, ESSResponse, TypeaheadAdapter, TypeaheadResult } from 'uitk-react-typeahead';
import lunr, { Index } from 'lunr';

// Register Korean,Japanese and Chinese stemmers in the Pipeline
lunr.Pipeline.registerFunction((word) => word, 'stemmer-ko');
lunr.Pipeline.registerFunction((word) => word, 'stemmer-jp');
lunr.Pipeline.registerFunction((word) => word, 'stemmer-zh');

interface SearchBoxTypeaheadAdapterConfigs {
  maxResults: number;
}

export class LunrSearchAdapter implements TypeaheadAdapter {
  private results: Array<TypeaheadResult>;
  private readonly categories: CategoryMap = {};
  private index: Index;

  constructor(
    private readonly indexFileURL: string,
    private readonly config?: SearchBoxTypeaheadAdapterConfigs
  ) {
    this.results = [];
    this.index = null;
  }

  onItemSelection(data: object): Promise<object> {
    return Promise.resolve(data);
  }

  getResults() {
    return this.results;
  }

  async fetchResponse(): Promise<ESSResponse> {
    return Promise.resolve({ sr: [] });
  }

  adaptResponseToTypeaheadResults(): TypeaheadResult[] {
    return new Array<TypeaheadResult>();
  }

  async getSearchIndex(indexFileURL: string) {
    if (this.index === null) {
      const response = await fetch(indexFileURL);
      this.index = lunr.Index.load(await response.json());
    }
    return this.index;
  }

  async fetchResults(term: string): Promise<TypeaheadResult[] | CategorizedResults[]> {
    const searchIndex = await this.getSearchIndex(this.indexFileURL);
    this.results = await search(term, searchIndex, this.config?.maxResults);

    return this.results;
  }

  async fetchResultsCategorized(term: string): Promise<CategorizedResults[]> {
    const searchIndex = await this.getSearchIndex(this.indexFileURL);
    this.results = await search(term, searchIndex, this.config?.maxResults);

    return groupSearchResults(this.results);
  }

  fetchRecentSearchResults(): Promise<TypeaheadResult[] | CategorizedResults[]> {
    throw new Error('Method not implemented.');
  }

  fetchRecentSearchResultsCategorized(): Promise<CategorizedResults[]> {
    throw new Error('Method not implemented.');
  }

  fetchAlternativeResults(): Promise<TypeaheadResult[] | CategorizedResults[]> {
    return Promise.resolve([]);
  }

  logResponse(results: unknown): Promise<unknown> {
    // console.debug('Search box response', results);
    return Promise.resolve(results);
  }

  typeaheadDialogOpened() {}

  logSuggestionShown() {}

  logTypeaheadDismiss(): void {}

  setTypeaheadCategories(): void {}

  getTypeaheadCategories(): CategoryMap {
    return this.categories;
  }
}

async function search(term, index, maxResults) {
  if (!term) {
    return [];
  }
  /*
  Escape Lunr-specific modifiers from the search input
  reference : https://lunrjs.com/docs/lunr.Index.html
  */
  const sanitizedTerm = term.replace(/[~^:]|^-|^\+/g, (match) => `\\${match}`);
  const matchingDocuments = await index.search(
    `${sanitizedTerm.trim()}^100 ${sanitizedTerm.trim()}*^50 ${sanitizedTerm.trim()}~2`
  );

  return matchingDocuments.slice(0, maxResults ?? matchingDocuments.length).map((matchingDocument) => {
    const documentInfo = JSON.parse(matchingDocument.ref);
    return {
      name: `<div class='search-result-location'>${documentInfo?.parentsTitles?.replace(
        /\//g,
        '<span class="search-result-location-separator"> › </span>'
      )}</div>`,
      title: documentInfo.title,
      value: documentInfo.path,
      icon: null,
      subtext: `<span class='search-result-title'>${documentInfo.title}</span>: ${documentInfo.intro || ''}`,
    } as TypeaheadResult;
  });
}

function groupSearchResults(results: TypeaheadResult[]): { heading: string; typeaheadResults: TypeaheadResult[] }[] {
  const groupedResults: { heading: string; typeaheadResults: TypeaheadResult[] }[] = [];

  for (const result of results) {
    // Grouping only happens at the root content level, e.g.: '/rapid/setup/hello' -> 'rapid'
    const heading = capitalizeFirstCharacter(extractContentRoot(result.value));
    const groupIndex = groupedResults.findIndex((group) => group.heading === heading);

    if (groupIndex === -1) {
      groupedResults.push({ heading, typeaheadResults: [result] });
    } else {
      groupedResults[groupIndex].typeaheadResults.push(result);
    }
  }

  return groupedResults;
}

function extractContentRoot(contentPath: string): string | null {
  const match = /^\/?(?<contentRoot>[^/]+)/.exec(contentPath);
  return match?.groups?.contentRoot ?? null;
}

function capitalizeFirstCharacter(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
